import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { SvgMinimize } from '../../components/SvgImports'
import {
  getGuidedViewPage,
  setShowFullPageViewer,
  setSummaryOrRfqOpenedFromPage,
} from '../../store/sessionDataSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { MainImageArea } from './MainImage/MainImageArea'
import { BreakpointWidthPx } from '../../css/BreakpointWidthPx'
import { BreakpointHeightPx } from '../../css/BreakpointHeightPx'
import { TdsButton, TdsIcon } from '@scania/tegel-react'
import { TextId, useTextsV2 } from '../../utils/useTexts'
import { useResizeObserver } from '../../utils/useResizeObserver'
import { Size2d } from '../../types/Size2d'
import { useRfqEysButtonState } from './RfqEysButtonState'
import { getUrlParametersToString } from '../../utils/getUrlParametersToString'
import { useNavigate } from 'react-router-dom'
import { getMarketLanguageState } from '../../store/appSlice'
import { buildRelativeSummaryPage } from '../../utils/UrlBuilders'
import { MainImageControlPanelMobile } from './MainImage/MainImageControlPanelMobile'
import { GuidedViewPageType } from '../../store/types'

interface FullPageViewerRootProps {
  $opacity: number
}

const FullPageImageViewerRoot = styled.div<FullPageViewerRootProps>`
  // TODO: Move this element to the root App.tsx and see if that affects
  // performance when clicking and dragging the mouse cursor.
  position: fixed;

  background-color: white;
  opacity: ${(props) => props.$opacity};
  transition-property: opacity;
  transition-duration: 300ms;
  top: 64px;
  left: 0;
  width: 100%;
  height: calc(100% - 64px);
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding-bottom: 60px;
  }
`

const CrossIcon = styled.div`
  position: absolute;
  border-radius: 3px; // Match the built-in controls.
  background-color: white;
  color: var(--tds-grey-800);
  top: 20px;
  right: 60px;
  width: 32px;
  height: 32px;
  cursor: pointer;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    top: 8px;
    right: 8px;
  }
`

const PannellumWrapper = styled.div`
  width: 100%;
  display: flex;
  flex: 1;
  padding-left: 40px;
  padding-right: 40px;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    padding-left: 8px;
    padding-right: 8px;
    flex-direction: column;
  }
`

const ButtonsWrapper = styled.div`
  width: 100%;
  height: 80px;
  display: flex;
  justify-content: end;
  gap: 16px;
  padding-right: 24px;

  button {
    min-width: 200px;
    display: flex;
    justify-content: center;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Large}px),
    screen and (max-height: ${BreakpointHeightPx.Large}px) {
    height: 60px;
  }

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

interface FullPageImageViewerProps {
  handleRfqOrEysClick: () => void
  navigateToMenu: (
    id: string,
    menuPath: string,
    gvPageToJumpTo: GuidedViewPageType,
  ) => void
}

export const FullPageImageViewer = ({
  navigateToMenu,
  handleRfqOrEysClick,
}: FullPageImageViewerProps): JSX.Element => {
  const dispatch = useAppDispatch()
  const t2 = useTextsV2()
  const navigate = useNavigate()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const size = useResizeObserver<Size2d>(rootRef, { width: 0, height: 0 })
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const guidedViewPage = useAppSelector(getGuidedViewPage)
  const { rfqOrEysButtonHidden, rfqOrEysButtonText, rfqOrEysButtonEnabled } =
    useRfqEysButtonState()
  const [isClosing, setIsClosing] = useState(false)
  const [rootOpacity, setRootOpacity] = useState(0)

  const handleKeydown = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        setIsClosing(true)
      }
    },
    [setIsClosing],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [handleKeydown])

  const handleRootTransitionEnd = useCallback(() => {
    if (isClosing) {
      dispatch(setShowFullPageViewer(false))
    }
  }, [isClosing, dispatch])

  useEffect(() => {
    setRootOpacity(isClosing ? 0 : 1)
  }, [isClosing])

  const handleCrossClick = useCallback(() => {
    setIsClosing(true)
  }, [setIsClosing])

  const handleSummaryClick = () => {
    if (!marketLanguage) {
      throw new Error('Expected market language.')
    }
    dispatch(setSummaryOrRfqOpenedFromPage(guidedViewPage))
    const relativeUrl = buildRelativeSummaryPage(marketLanguage)
    const params = relativeUrl.params
    const newUrl = relativeUrl.path + getUrlParametersToString(params)
    navigate(newUrl)
    dispatch(setShowFullPageViewer(false))
  }

  return (
    <FullPageImageViewerRoot
      $opacity={rootOpacity}
      onTransitionEnd={handleRootTransitionEnd}
      ref={rootRef}
    >
      <PannellumWrapper>
        <MainImageArea
          showImageControlPanel={true}
          showSideArrows={true}
          navigateToMenu={navigateToMenu}
          showFullViewIcon={false}
        />
        <CrossIcon onClick={handleCrossClick}>
          <SvgMinimize />
        </CrossIcon>
        <MainImageControlPanelMobile showIcons={false} />
      </PannellumWrapper>
      <ButtonsWrapper>
        {!rfqOrEysButtonHidden && (
          <TdsButton
            variant="primary"
            text={rfqOrEysButtonText}
            size={size.width < BreakpointWidthPx.Large ? 'sm' : 'lg'}
            onClick={handleRfqOrEysClick}
            disabled={!rfqOrEysButtonEnabled}
          >
            <TdsIcon slot="icon" size="20px" name="message" />
          </TdsButton>
        )}
        <TdsButton
          variant="secondary"
          text={t2(TextId.LABEL_ACTION_SUMMARY)}
          size={size.width < BreakpointWidthPx.Large ? 'sm' : 'lg'}
          onClick={handleSummaryClick}
        >
          <TdsIcon slot="icon" size="20px" name="document" />
        </TdsButton>
      </ButtonsWrapper>
    </FullPageImageViewerRoot>
  )
}
